import { createServerFn } from "@tanstack/react-start";
import { getRequest, getRequestHeader, getCookie, setCookie } from "@tanstack/react-start/server";
import { resolveApproxGeo, jitter, anonymizeCoord } from "./user-location.geo";

/**
 * Anonymous visitor tracking for the public map. Stores only a random
 * per-browser token in a cookie and a fuzzed approximate location + country
 * code. No IP, no user id, no identifying data.
 */

const COOKIE_NAME = "lc_visitor";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 400; // ~13 months

function readOrCreateToken(): string {
  const existing = getCookie(COOKIE_NAME);
  if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
  const token = crypto.randomUUID();
  setCookie(COOKIE_NAME, token, {
    httpOnly: true,
    sameSite: "lax",
    secure: true,
    maxAge: COOKIE_MAX_AGE,
    path: "/",
  });
  return token;
}

/**
 * Public (no auth). Called from the map page when the visitor is not signed
 * in. Idempotent: refreshes the row's last_seen_at on every call.
 */
export const trackAnonymousVisit = createServerFn({ method: "POST" }).handler(async () => {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const token = readOrCreateToken();

    // Reuse existing fresh row: don't burn the geo fallback more than every 6h
    // per anonymous visitor.
    const { data: existing } = await supabaseAdmin
      .from("anon_visitor_locations")
      .select("last_seen_at, approx_country")
      .eq("session_token", token)
      .maybeSingle();

    if (
      existing?.last_seen_at &&
      Date.now() - new Date(existing.last_seen_at).getTime() < 6 * 60 * 60 * 1000
    ) {
      await supabaseAdmin
        .from("anon_visitor_locations")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("session_token", token);
      return { ok: true as const, source: "cached" as const, country: existing.approx_country };
    }

    const req = getRequest() as Request & {
      cf?: { latitude?: string; longitude?: string; country?: string };
    };
    const headerLookup = (name: string) => getRequestHeader(name) ?? null;
    const geo = await resolveApproxGeo({
      cf: req.cf,
      getHeader: headerLookup,
      fetchImpl: fetch,
    });
    if (!geo.ok) {
      console.warn("[trackAnonymousVisit] no geo", { reason: geo.reason });
      return { ok: false as const, reason: geo.reason };
    }

    const row = {
      session_token: token,
      approx_latitude: jitter(geo.latitude),
      approx_longitude: jitter(geo.longitude),
      approx_country: geo.country,
      last_seen_at: new Date().toISOString(),
    };
    const { error } = await supabaseAdmin
      .from("anon_visitor_locations")
      .upsert(row, { onConflict: "session_token" });
    if (error) {
      console.error("[trackAnonymousVisit] upsert failed", error);
      return { ok: false as const, reason: "db-error" as const, message: error.message };
    }
    return { ok: true as const, source: geo.source, country: geo.country };
  } catch (e) {
    const msg = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    console.error("[trackAnonymousVisit] fatal", msg, e);
    return { ok: false as const, reason: "exception" as const, message: msg };
  }
});

export type AnonVisitorLocation = {
  session_token: string;
  latitude: number;
  longitude: number;
  country: string | null;
};

/**
 * Public: last-24h anonymized anonymous visitor pins.
 *
 * Privacy guarantees applied here (in addition to the ±0.03° jitter applied
 * at write time in `trackAnonymousVisit`):
 *   1. Coordinates are snapped to a ~11 km grid via `anonymizeCoord`.
 *   2. A deterministic per-token sub-cell offset prevents perfect stacking
 *      without ever revealing the stored precise value.
 *   3. The raw `session_token` is replaced with an opaque hash so the cookie
 *      value never leaves the server.
 */
export const listAnonymousVisitorLocations = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.rpc("get_public_anon_visitor_locations");
  if (error) throw error;
  const rows = (data ?? []) as Array<{
    session_token: string;
    latitude: number;
    longitude: number;
    country: string | null;
  }>;
  return rows.map((r, i) => {
    // Opaque per-pin id — never expose the raw session cookie value.
    const seed = r.session_token ?? `idx-${i}`;
    let h = 5381;
    for (let j = 0; j < seed.length; j++) h = ((h << 5) + h + seed.charCodeAt(j)) | 0;
    const opaqueId = `anon-${(h >>> 0).toString(36)}`;
    return {
      session_token: opaqueId,
      latitude: anonymizeCoord(r.latitude, seed, "lat"),
      longitude: anonymizeCoord(r.longitude, seed, "lng"),
      country: r.country,
    };
  }) as AnonVisitorLocation[];
});

